import {
  Card,
  Typography,
  Box,
  SvgIcon,
  useTheme,
  Grid2 as Grid,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CardHeader,
  CardContent
} from '@mui/material';
import { ArrowRight, FileText, Maximize2, Shield } from 'lucide-react';
import { CircleCheck } from 'lucide-react-new';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Page from 'src/components/Page';
import { makeStyles } from 'tss-react/mui';
import { SECURITY_CONTROL_ITEMS } from './constants';
import SecurityAndControlsDialog from './SecurityAndControlsDialog';

const useStyles = makeStyles()(theme => ({
  metalRoot: {
    [theme.breakpoints.up('md')]: {
      padding: `0 ${theme.spacing(7)} ${theme.spacing(5)}`
    }
  }
}));

const SecurityAndControls = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const mediumDevice = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedInfoItem, setSelectedInfoItem] = useState(null);
  const [infoItemDialogOpen, setInfoItemDialogOpen] = useState(false);

  const handleOpenInfoItemDialog = infoItem => {
    setSelectedInfoItem(infoItem);
    setInfoItemDialogOpen(true);
  };

  const handleCloseInfoItemDialog = () => {
    setSelectedInfoItem(null);
    setInfoItemDialogOpen(false);
  };

  return (
    <Page className={classes.metalRoot} title={'Security & Controls'}>
      <Card
        style={{
          backgroundColor: 'var(--neutral-2)',
          padding: '10px 0'
        }}
      >
        <Grid
          p={theme.spacing(2)}
          container
          spacing={3}
          sx={{
            gridTemplateColumns: 'repeat(2, 1fr)',
            gridTemplateRows: '300px'
          }}
        >
          <Grid size={mediumDevice ? 12 : 6}>
            <Card
              variant="outlined"
              style={{
                backgroundColor: 'var(--neutral-3',
                padding: theme.spacing(2),
                height: '100%'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center'
                }}
              >
                <FileText size={22} />
                <Typography style={{ fontSize: 22, fontWeight: 'bold' }}>
                  Overview
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  minHeight: 115,
                  alignItems: 'center',
                  marginTop: '10px'
                }}
              >
                <Typography variant="body1">
                  Welcome to the Trust Center. Our commitment to data privacy
                  and security is embedded in every part of our business. Use
                  this Trust Center to learn about our security posture.
                </Typography>
              </Box>
            </Card>
          </Grid>
          <Grid size={mediumDevice ? 12 : 6}>
            <Card
              style={{
                backgroundColor: 'var(--neutral-3',
                padding: theme.spacing(2),
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid var(--color-border)',
                cursor: 'pointer',
                height: '100%'
              }}
              onClick={() => handleOpenInfoItemDialog('compliance')}
            >
              <CardHeader
                style={{ padding: 0 }}
                title={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center'
                      }}
                    >
                      <Shield size={22} />
                      <Typography style={{ fontSize: 22, fontWeight: 'bold' }}>
                        Compliance
                      </Typography>
                    </Box>
                    <Maximize2 size={22} color="var(--color-icon)" />
                  </Box>
                }
              />
              <CardContent
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  minHeight: 120,
                  alignItems: 'center'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(2)
                  }}
                >
                  <img
                    src="/static/images/soc.svg"
                    alt="SOC Logo"
                    style={{
                      width: 81,
                      height: 80
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: '5px' }}>
                    <CircleCheck color="var(--accent-11)" size={22} />
                    <Typography style={{ fontWeight: 'bold' }}>
                      SOC 2
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Grid container p={theme.spacing(2)} spacing={3}>
          {Object.keys(SECURITY_CONTROL_ITEMS).map(item => {
            if (item === 'compliance') {
              return null;
            }
            return (
              <Grid size={mediumDevice ? 12 : 4} key={item}>
                <Card
                  style={{
                    minHeight: '250px',
                    height: '100%',
                    backgroundColor: 'var(--neutral-3',
                    padding: theme.spacing(2),
                    border: '1px solid var(--color-border)',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onClick={() => handleOpenInfoItemDialog(item)}
                >
                  <CardHeader
                    style={{ paddingLeft: 0, paddingBottom: 5 }}
                    title={
                      <Box sx={{ display: 'flex', gap: '10px' }}>
                        <SvgIcon>{SECURITY_CONTROL_ITEMS[item].icon}</SvgIcon>
                        <Typography variant="h4">{t(item)}</Typography>
                      </Box>
                    }
                  />
                  <Box pb={'32px'}>
                    <List>
                      {SECURITY_CONTROL_ITEMS[item].summaryDetails.map(
                        (detail, index) => {
                          return (
                            <ListItem
                              sx={{ paddingLeft: 0 }}
                              key={`${item}-${detail}-${index}`}
                            >
                              <ListItemIcon>
                                <CircleCheck
                                  color="var(--accent-11)"
                                  size={22}
                                />
                              </ListItemIcon>
                              <ListItemText primary={detail} />
                            </ListItem>
                          );
                        }
                      )}
                    </List>
                  </Box>
                  <Box
                    sx={{
                      textTransform: 'capitalize',
                      paddingLeft: 0,
                      display: 'flex',
                      gap: '10px',
                      paddingTop: '10px',
                      position: 'absolute',
                      bottom: 22,
                      left: 18
                    }}
                  >
                    <Typography style={{ color: 'white' }}>
                      View More
                    </Typography>
                    <ArrowRight size={22} />
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Card>

      <SecurityAndControlsDialog
        open={infoItemDialogOpen}
        onClose={handleCloseInfoItemDialog}
        selectedInfoItem={selectedInfoItem}
      />
    </Page>
  );
};

export default SecurityAndControls;
